import React from "react";
import { compose } from "redux";

import SEOHelper from "../lib/SEOHelper/SEOHelper";
import withMarketTranslations from "./withMarketTranslations";
import withTranslations from "./withTranslations";
import withUserData from "./withUserData";

/**
 * Loads locale-aware stock section SEO slugs for notification deep links.
 * Reads market from `marketId`, `item.config.market`, or `config.market`.
 */
export default function withPreparedSectionSlugList(WrappedComponent) {
  class WithPreparedSectionSlugList extends React.Component {
    state = {
      preparedSlugList: undefined
    };

    getMarketId() {
      const { marketId, item, config } = this.props;
      return marketId ?? item?.config?.market ?? config?.market;
    }

    async loadPreparedSlugList() {
      const { locale, authToken, marketTranslations } = this.props;
      const marketId = this.getMarketId();

      if (!marketId) {
        this.setState({ preparedSlugList: {} });
        return;
      }

      try {
        const preparedSlugList = await SEOHelper.getPreparedSectionSlugList({
          locale,
          authToken,
          marketId,
          marketTranslations
        });

        if (!this._isMounted) {
          return;
        }

        this.setState({ preparedSlugList });
      } catch (error) {
        if (!this._isMounted) {
          return;
        }

        console.warn(
          "Failed to load stock section SEO slug list; falling back to section ids",
          error
        );
        // Fall back to section ids in URLs if SEO list fails.
        this.setState({ preparedSlugList: {} });
      }
    }

    componentDidMount() {
      this._isMounted = true;
      void this.loadPreparedSlugList();
    }

    componentDidUpdate(prevProps) {
      const prevMarketId =
        prevProps.marketId ??
        prevProps.item?.config?.market ??
        prevProps.config?.market;
      const marketId = this.getMarketId();

      if (
        prevMarketId !== marketId ||
        prevProps.locale !== this.props.locale ||
        prevProps.authToken !== this.props.authToken ||
        prevProps.marketTranslations !== this.props.marketTranslations
      ) {
        void this.loadPreparedSlugList();
      }
    }

    componentWillUnmount() {
      this._isMounted = false;
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          preparedSlugList={this.state.preparedSlugList}
        />
      );
    }
  }

  return compose(
    withUserData,
    withMarketTranslations,
    withTranslations
  )(WithPreparedSectionSlugList);
}
